// Lazy import to avoid circular dep at module init time
// Native-aware WebSocket URL (prod host in the Capacitor app; same-origin on web).
import { WS_URL } from '../utils/apiBase.js';

class ChatSocket {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this.maxReconnects = 5;
    this.currentTeamId = null;
  }

  connect(token) {
    if (this.ws?.readyState === WebSocket.OPEN) return;

    const wsUrl = WS_URL;
    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('🔌 WebSocket connected');
      this.reconnectAttempts = 0;
      // Always use the freshest token at the time of (re)connection
      const currentToken = localStorage.getItem('token') || token;
      this.send({ type: 'AUTH', token: currentToken });
      if (this.currentTeamId) {
        this.joinTeam(this.currentTeamId);
      }
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        // Fix #11: server sends AUTH_EXPIRED when the access token has expired.
        // Refresh the HTTP token silently and re-authenticate the socket so the
        // user doesn't lose their chat connection after the 15-minute token window.
        if (msg.type === 'AUTH_EXPIRED') {
          this._handleAuthExpired();
          return;
        }

        this.emit(msg.type, msg.data);
        this.emit('*', msg);
      } catch (e) {
        console.error('WS parse error:', e);
      }
    };

    this.ws.onclose = () => {
      console.log('🔌 WebSocket disconnected');
      this.scheduleReconnect();
    };

    this.ws.onerror = (err) => {
      console.error('WS error:', err);
    };
  }

  // Silently refresh the HTTP access token then re-auth the socket.
  async _handleAuthExpired() {
    // 2026-11-02 ("I keep getting logged out"): this used to run its OWN
    // /auth/refresh beside the one in api.js - two refreshes with the same
    // token within a second - and wiped the session on ANY failure,
    // including a slow server (15 s timeout) or a rate-limited retry. Now
    // it shares api.js's single in-flight refresh, and only a definitive
    // verdict from the server (401/403) ends the session.
    try {
      const mod = await import('./api.js');
      if (!mod.hasSession()) {
        window.location.href = '/login';
        return;
      }
      const accessToken = await mod.refreshSession();
      // Re-authenticate on the existing open socket
      this.send({ type: 'AUTH', token: accessToken });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('refreshToken_prev');
        localStorage.removeItem('refreshToken_prev_expiry');
        if (!window.location.pathname.startsWith('/login')) window.location.href = '/login';
      }
      // anything else (timeout, 429, 5xx, no network): keep the session;
      // the next reconnect picks up whatever token the app refreshes to
    }
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnects) {
      // v2.5.125 — emit a give-up event so the UI can show the user
      // that real-time features are disabled. Previously this would
      // silently stop reconnecting and the user would never know why
      // chat messages weren't arriving live. Listeners that care
      // (e.g. a status pip in the layout) can subscribe via
      // chatSocket.on('GIVE_UP', ...).
      this.emit('GIVE_UP', { attempts: this.reconnectAttempts });
      return;
    }
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectTimer = setTimeout(() => {
      // Fix #4: don't reconnect after logout — token is gone
      if (!localStorage.getItem('token')) return;
      this.reconnectAttempts++;
      this.connect(); // token will be read fresh from localStorage in onopen
    }, delay);
  }

  joinTeam(teamId) {
    this.currentTeamId = teamId;
    this.send({ type: 'JOIN_TEAM', teamId });
  }

  sendTyping(isTyping) {
    this.send({ type: 'TYPING', isTyping });
  }

  send(data) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event).add(callback);
    return () => this.listeners.get(event)?.delete(callback);
  }

  emit(event, data) {
    this.listeners.get(event)?.forEach(cb => cb(data));
  }

  disconnect() {
    // FIX #46: clear all socket state on disconnect, not just the connection.
    // Without this, logging out then logging in as a different user would:
    //   (a) auto-rejoin the new user to the OLD user's team chat (currentTeamId
    //       persists), leaking messages across accounts, AND
    //   (b) carry over reconnectAttempts so the new session might immediately
    //       hit the max-reconnects ceiling.
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this.currentTeamId = null;
    this.ws?.close();
    this.ws = null;
  }
}

export const chatSocket = new ChatSocket();
